import React, { useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  CartesianGrid,
  Line,
  LineChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  BiBriefcase,
  BiCalendar,
  BiCheckCircle,
  BiFile,
  BiPlus,
  BiRupee,
  BiTargetLock,
  BiTimeFive
} from 'react-icons/bi';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { TENDER_APP_NAME } from '../../../config/branding';
import PageHelp from '../../../components/workspace/PageHelp';
import './TenderAppHome.scss';

const ICONS = {
  identified: BiFile,
  active: BiBriefcase,
  pipeline: BiRupee,
  due: BiTimeFive,
  win: BiTargetLock,
  submitted: BiCheckCircle
};

const MONTH = {
  kpis: [
    { key: 'identified', label: 'Identified', value: '18', hint: 'New this month' },
    { key: 'active', label: 'Active bids', value: '11', hint: 'In preparation' },
    { key: 'pipeline', label: 'Open pipeline', value: '₹86 Cr', hint: 'Estimated value' },
    { key: 'due', label: 'Due this week', value: '3', hint: 'Need a decision' },
    { key: 'submitted', label: 'Submitted', value: '4', hint: 'Awaiting result' },
    { key: 'win', label: 'Win rate', value: '28%', hint: 'Trailing 90 days' }
  ],
  trend: [
    { month: 'May', identified: 6, submitted: 2, awarded: 1 },
    { month: 'Jun', identified: 8, submitted: 3, awarded: 1 },
    { month: 'Jul', identified: 5, submitted: 4, awarded: 2 },
    { month: 'Aug', identified: 7, submitted: 3, awarded: 1 },
    { month: 'Sep', identified: 11, submitted: 5, awarded: 2 },
    { month: 'Oct', identified: 18, submitted: 4, awarded: 1 }
  ],
  stages: [
    { label: 'Identified', count: 18 },
    { label: 'Assessing', count: 7 },
    { label: 'Preparing', count: 6 },
    { label: 'Under review', count: 3 },
    { label: 'Submitted', count: 4 }
  ],
  decisions: [
    { label: 'Awarded', value: '3', tone: 'sage' },
    { label: 'In review', value: '3', tone: 'sand' },
    { label: 'Submitted', value: '4', tone: 'mint' },
    { label: 'Lost', value: '1', tone: 'rose' }
  ],
  mix: [
    { label: 'Awarded', count: 3 },
    { label: 'Submitted', count: 4 },
    { label: 'In preparation', count: 11 },
    { label: 'Lost', count: 1 }
  ]
};

const QUARTER = {
  ...MONTH,
  kpis: [
    { key: 'identified', label: 'Identified', value: '41', hint: 'This quarter' },
    { key: 'active', label: 'Active bids', value: '19', hint: 'In preparation' },
    { key: 'pipeline', label: 'Open pipeline', value: '₹214 Cr', hint: 'Estimated value' },
    { key: 'due', label: 'Due this week', value: '3', hint: 'Need a decision' },
    { key: 'submitted', label: 'Submitted', value: '12', hint: 'Awaiting result' },
    { key: 'win', label: 'Win rate', value: '31%', hint: 'Trailing 90 days' }
  ],
  stages: [
    { label: 'Identified', count: 41 },
    { label: 'Assessing', count: 14 },
    { label: 'Preparing', count: 11 },
    { label: 'Under review', count: 6 },
    { label: 'Submitted', count: 12 }
  ]
};

const FEED = [
  { id: '1', kind: 'pursuit', mark: 'N', title: 'NHAI DPR — Mumbai–Pune upgrade', meta: 'DCSL-2026-001 · Identified', when: 'Today', to: '/apps/tender/register' },
  { id: '2', kind: 'decision', mark: 'M', title: 'MMRDA PMC — Eastern freeway', meta: 'Go / no-go before Friday', when: 'Today', to: '/apps/tender/approvals' },
  { id: '3', kind: 'deadline', mark: 'P', title: 'PWD design — Pune interchange', meta: 'Submission pack due', when: '17 Oct', to: '/apps/tender/register' },
  { id: '4', kind: 'pursuit', mark: 'B', title: 'MSRDC Independent Engineer — creek bridge', meta: 'DCSL-2026-003 · Preparing', when: '29 Sep', to: '/apps/tender/register' },
  { id: '5', kind: 'pursuit', mark: 'W', title: 'MCGM Authority Engineer — water mains', meta: 'DCSL-2026-004 · Under review', when: '29 Sep', to: '/apps/tender/approvals' },
  { id: '6', kind: 'deadline', mark: 'Q', title: 'NHAI quality audit — Samruddhi', meta: 'Clarification window closes', when: '22 Oct', to: '/apps/tender/register' },
  { id: '7', kind: 'decision', mark: 'J', title: 'JNPA feasibility — port access road', meta: 'Recorded as lost', when: '21 Sep', to: '/apps/tender/register' }
];

const PURSUITS = [
  { ref: 'DCSL-2026-001', title: 'NHAI DPR — Mumbai–Pune Expressway', client: 'NHAI', value: '₹186 Cr', stage: 'Identified' },
  { ref: 'DCSL-2026-002', title: 'MMRDA PMC — Eastern freeway extension', client: 'MMRDA', value: '₹94 Cr', stage: 'Assessing' },
  { ref: 'DCSL-2026-008', title: 'MahaMetro PMC — Nagpur metro civil works', client: 'MahaMetro', value: '₹73 Cr', stage: 'Preparing' },
  { ref: 'DCSL-2026-003', title: 'MSRDC Independent Engineer — creek bridge', client: 'MSRDC', value: '₹62 Cr', stage: 'Preparing' },
  { ref: 'DCSL-2026-004', title: 'MCGM Authority Engineer — water mains', client: 'BMC', value: '₹41 Cr', stage: 'Review' }
];

const ATTENTION = [
  { title: 'NHAI DPR, MMRDA freeway, water mains', meta: 'Go / no-go before Friday', tone: 'risk' },
  { title: 'Two bids missed the planned submit date', meta: 'Update the stage or the internal plan', tone: 'risk' },
  { title: 'Eligibility evidence still open', meta: 'Two active bids scored below 70', tone: 'watch' },
  { title: 'Samruddhi quality audit clarification', meta: 'Window closes 22 October', tone: 'ok' },
  { title: 'PWD Pune interchange awarded', meta: 'Move the team onto the assignment', tone: 'ok' }
];

const COLUMNS = [
  {
    key: 'identified',
    label: 'Identified',
    items: [
      { ref: 'DCSL-2026-001', title: 'NHAI · Mumbai–Pune DPR', when: 'Today' },
      { ref: 'DCSL-2026-007', title: 'JNPA · Port access road', when: '21 Sep' }
    ]
  },
  {
    key: 'preparing',
    label: 'Preparing',
    items: [
      { ref: 'DCSL-2026-003', title: 'MSRDC · Creek bridge', when: '29 Sep' },
      { ref: 'DCSL-2026-008', title: 'MahaMetro · Nagpur civil', when: '24 Sep' },
      { ref: 'DCSL-2026-002', title: 'MMRDA · Eastern freeway', when: '05 Oct' }
    ]
  },
  {
    key: 'submitted',
    label: 'Submitted',
    items: [
      { ref: 'DCSL-2026-005', title: 'NHAI · Samruddhi audit', when: '12 Oct' },
      { ref: 'DCSL-2026-006', title: 'PWD · Pune interchange', when: 'Awarded' }
    ]
  }
];

const FLOW = [
  { n: '01', title: 'Capture', text: 'Record the opportunity in the tender register with client, value and deadline.', to: '/apps/tender/register' },
  { n: '02', title: 'Assess', text: 'Check eligibility, credentials and whether the firm can deliver the scope.', to: '/apps/tender/knowledge' },
  { n: '03', title: 'Decide', text: 'Go / no-go. A no-go stops the pursuit. A go opens preparation.', to: '/apps/tender/approvals' },
  { n: '04', title: 'Prepare', text: 'Build the technical response from the knowledge bank and named personnel.', to: '/apps/tender/personnel' },
  { n: '05', title: 'Price', text: 'Prepare the commercial estimate, rates and effort.', to: '/apps/tender/masters/rates' },
  { n: '06', title: 'Approve', text: 'Technical, commercial and bid approvals before anything leaves the firm.', to: '/apps/tender/approvals' },
  { n: '07', title: 'Submit', text: 'Lock the submission pack and record that the bid has gone to the client.', to: '/apps/tender/register' },
  { n: '08', title: 'Close', text: 'Award or loss. Awarded work continues into assignment and timesheets.', to: '/apps/tender/timesheets' }
];

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'pursuit', label: 'Pursuits' },
  { id: 'decision', label: 'Decisions' },
  { id: 'deadline', label: 'Deadlines' }
];

const MARKED_DAYS = { 8: 'deadline', 15: 'today', 17: 'deadline', 22: 'deadline' };

function roleLabel(user) {
  const raw = Array.isArray(user?.roles) ? user.roles[0] : user?.role;
  const text = typeof raw === 'string' ? raw : (raw?.name || 'Tender team');
  return text.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function initials(name) {
  return String(name || 'D')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();
}

function monthGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const start = first.getDay();
  const days = new Date(year, monthIndex + 1, 0).getDate();
  const cells = Array.from({ length: start }, () => null);
  for (let day = 1; day <= days; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function StatusBars({ rows }) {
  const max = Math.max(...rows.map((row) => row.count), 1);
  return (
    <ul className="tdash-bars">
      {rows.map((row) => (
        <li key={row.label}>
          <span>{row.label}</span>
          <i><b style={{ width: `${Math.max(8, (row.count / max) * 100)}%` }} /></i>
          <strong>{row.count}</strong>
        </li>
      ))}
    </ul>
  );
}

const TenderAppHome = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [period, setPeriod] = useState('month');
  const [tab, setTab] = useState('all');
  const name = user?.name || 'there';
  const pack = period === 'quarter' ? QUARTER : MONTH;
  const cells = useMemo(() => monthGrid(2026, 9), []);
  const feed = tab === 'all' ? FEED : FEED.filter((item) => item.kind === tab);
  const mixTotal = MONTH.mix.reduce((sum, row) => sum + row.count, 0);

  useDocumentTitle(TENDER_APP_NAME);

  return (
    <div className="tdash">
      <div className="tdash__note">
        <p className="tdash__sample">Sample figures for layout preview. Live register totals will replace these counts.</p>
        <PageHelp topic="overview" />
      </div>

      <div className="tdash-board">
        <div className="tdash-board__primary">
          <div className="tdash-statrow">
            <article className="tdash-welcome">
              <span className="tdash-welcome__mark" aria-hidden>{initials(user?.name)}</span>
              <div>
                <p>Welcome</p>
                <h1>{name}</h1>
                <small>{roleLabel(user)}</small>
              </div>
              <div className="tdash-welcome__actions">
                <Dropdown align="end">
                  <Dropdown.Toggle variant="light" size="sm" className="tdash__period" id="tdash-period">
                    <BiCalendar aria-hidden />
                    {period === 'quarter' ? 'This quarter' : 'Oct 2026'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item active={period === 'month'} onClick={() => setPeriod('month')}>This month</Dropdown.Item>
                    <Dropdown.Item active={period === 'quarter'} onClick={() => setPeriod('quarter')}>This quarter</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <button type="button" className="tdash__create" onClick={() => navigate('/apps/tender/register/new')}>
                  <BiPlus aria-hidden />
                  New tender
                </button>
              </div>
            </article>
            {pack.kpis.slice(0, 2).map((kpi) => {
              const Icon = ICONS[kpi.key];
              return (
                <article key={kpi.key} className="tdash-tile">
                  <div>
                    <strong>{kpi.value}</strong>
                    <span>{kpi.label}</span>
                    <small>{kpi.hint}</small>
                  </div>
                  <i aria-hidden><Icon /></i>
                </article>
              );
            })}
          </div>

          <div className="tdash-statrow tdash-statrow--four">
            {pack.kpis.slice(2).map((kpi) => {
              const Icon = ICONS[kpi.key];
              return (
                <article key={kpi.key} className="tdash-tile">
                  <div>
                    <strong>{kpi.value}</strong>
                    <span>{kpi.label}</span>
                    <small>{kpi.hint}</small>
                  </div>
                  <i aria-hidden><Icon /></i>
                </article>
              );
            })}
          </div>

          <div className="tdash-split">
            <article className="tdash-panel">
              <div className="tdash-panel__head">
                <h2>Calendar</h2>
                <span>Oct 2026</span>
              </div>
              <div className="tdash-cal" aria-label="October 2026">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => (
                  <span key={`${label}-${index}`} className="tdash-cal__dow">{label}</span>
                ))}
                {cells.map((day, index) => (
                  <span
                    key={day ? `d-${day}` : `e-${index}`}
                    className={`tdash-cal__day${day ? '' : ' is-empty'}${MARKED_DAYS[day] ? ` is-${MARKED_DAYS[day]}` : ''}`}
                  >
                    {day || ''}
                    {day && MARKED_DAYS[day] === 'deadline' ? <i /> : null}
                  </span>
                ))}
              </div>
            </article>

            <article className="tdash-panel">
              <div className="tdash-panel__head">
                <h2>Pipeline trend</h2>
                <span className="tdash-legend">
                  <i className="is-identified" /> Identified
                  <i className="is-submitted" /> Submitted
                  <i className="is-awarded" /> Awarded
                </span>
              </div>
              <div className="tdash-chart">
                <ResponsiveContainer width="100%" height={210}>
                  <LineChart data={pack.trend} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid stroke="#f0eeec" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#667085' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#667085' }} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="identified" name="Identified" stroke="#c4a35a" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="submitted" name="Submitted" stroke="#a63726" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="awarded" name="Awarded" stroke="#448065" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </article>
          </div>
        </div>

        <aside className="tdash-rail">
          <div className="tdash-tabs" role="tablist">
            {TABS.map((item) => {
              const count = item.id === 'all' ? FEED.length : FEED.filter((row) => row.kind === item.id).length;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.id}
                  className={tab === item.id ? 'is-on' : ''}
                  onClick={() => setTab(item.id)}
                >
                  {item.label}
                  <em>{count}</em>
                </button>
              );
            })}
          </div>
          <ul className="tdash-feed">
            {feed.map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => navigate(item.to)}>
                  <span className={`tdash-feed__mark is-${item.kind}`} aria-hidden>{item.mark}</span>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.meta}</small>
                  </span>
                  <em>{item.when}</em>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <section className="tdash-pair">
        <article className="tdash-panel">
          <div className="tdash-panel__head">
            <h2>Pipeline overview</h2>
            <button type="button" onClick={() => navigate('/apps/tender/register')}>View all</button>
          </div>
          <div className="tdash-overview">
            <div>
              <p className="tdash-overview__label">Stage mix</p>
              <StatusBars rows={pack.stages.map((row) => ({ label: row.label, count: row.count }))} />
            </div>
            <div>
              <p className="tdash-overview__label">Monthly submissions</p>
              <div className="tdash-chart">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={pack.trend} margin={{ top: 8, right: 4, left: -22, bottom: 0 }}>
                    <CartesianGrid stroke="#f0eeec" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#667085' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#667085' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(166, 55, 38, 0.05)' }} />
                    <Bar dataKey="submitted" name="Submitted" fill="#a63726" radius={[2, 2, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </article>

        <article className="tdash-panel">
          <div className="tdash-panel__head">
            <h2>Decision overview</h2>
            <button type="button" onClick={() => navigate('/apps/tender/approvals')}>View all</button>
          </div>
          <div className="tdash-minis">
            {MONTH.decisions.map((item) => (
              <div key={item.label} className={`tdash-mini tdash-mini--${item.tone}`}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
          <div className="tdash-overview">
            <StatusBars rows={MONTH.mix} />
            <div className="tdash-donut-wrap">
              <div
                className="tdash-donut"
                style={{ background: 'conic-gradient(#448065 0 16%, #a63726 16% 37%, #c4a35a 37% 95%, #d9cfc8 95% 100%)' }}
                aria-hidden
              />
              <ul>
                {MONTH.mix.map((row) => (
                  <li key={row.label}><span>{row.label}</span><strong>{row.count}</strong></li>
                ))}
                <li><span>Open pursuits</span><strong>{mixTotal}</strong></li>
              </ul>
            </div>
          </div>
        </article>
      </section>

      <section className="tdash-trio">
        <article className="tdash-panel">
          <div className="tdash-panel__head">
            <h2>Register snapshot</h2>
            <button type="button" onClick={() => navigate('/apps/tender/register')}>View all</button>
          </div>
          <ol className="tdash-rank">
            {PURSUITS.map((row, index) => (
              <li key={row.ref}>
                <button type="button" onClick={() => navigate('/apps/tender/register')}>
                  <em>{index + 1}</em>
                  <span>
                    <strong>{row.title}</strong>
                    <small>{row.client} · {row.ref}</small>
                  </span>
                  <b>{row.value}</b>
                  <i>{row.stage}</i>
                </button>
              </li>
            ))}
          </ol>
        </article>

        <article className="tdash-panel">
          <div className="tdash-panel__head">
            <h2>Needs attention</h2>
            <button type="button" onClick={() => navigate('/apps/tender/approvals')}>Approvals</button>
          </div>
          <ol className="tdash-rank">
            {ATTENTION.map((row, index) => (
              <li key={row.title}>
                <button type="button" onClick={() => navigate('/apps/tender/approvals')}>
                  <em>{index + 1}</em>
                  <span>
                    <strong>{row.title}</strong>
                    <small>{row.meta}</small>
                  </span>
                  <i className={`is-${row.tone}`}>{row.tone === 'ok' ? 'Clear' : row.tone === 'watch' ? 'Watch' : 'Due'}</i>
                </button>
              </li>
            ))}
          </ol>
        </article>

        <article className="tdash-panel">
          <div className="tdash-panel__head">
            <h2>Pipeline activity</h2>
            <button type="button" onClick={() => navigate('/apps/tender/register')}>Register</button>
          </div>
          <div className="tdash-cols">
            {COLUMNS.map((column) => (
              <div key={column.key}>
                <p>{column.label} <em>{column.items.length}</em></p>
                <ul>
                  {column.items.map((item) => (
                    <li key={item.ref}>
                      <strong>{item.ref}</strong>
                      <span>{item.title}</span>
                      <small>{item.when}</small>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="tdash__flow" aria-labelledby="tender-process-flow">
        <div className="tdash__flow-head">
          <div>
            <h2 id="tender-process-flow">How a tender moves</h2>
            <p>One pursuit passes through these steps. A no-go ends the flow at Decide. An award continues into delivery.</p>
          </div>
        </div>
        <ol className="tdash__flow-track">
          {FLOW.map((step, index) => (
            <li key={step.n}>
              <button type="button" className="tdash__flow-step" onClick={() => navigate(step.to)}>
                <span className="tdash__flow-index">{step.n}</span>
                <strong>{step.title}</strong>
                <span>{step.text}</span>
              </button>
              {index < FLOW.length - 1 ? <span className="tdash__flow-join" aria-hidden /> : null}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};

export default TenderAppHome;
